import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';

/**
 * The server's `Handwriting` canvas. Points are whole numbers in this space whatever size the pad is
 * drawn at, so a phone and a desktop produce comparable strokes and the server can refuse anything
 * off the pad without knowing the screen.
 */
export const HANDWRITING_WIDTH = 600;
export const HANDWRITING_HEIGHT = 240;

/** The server's `MinExtent`: how far the ink has to reach before it is a signature and not a tap. */
const MIN_EXTENT = 20;

/** The server's `MaxPoints`. Past it, the pad stops taking ink rather than letting the request fail. */
const MAX_POINTS = 5000;

/** Points closer than this to the last one add bytes and nothing a person could see. */
const MIN_STEP = 2;

const INK_WIDTH = 3;

/**
 * A pad the customer signs on with a mouse, a finger or a pen.
 *
 * **It hands back strokes, never an image.** Each press of the pen is one array of `[x, y]` points
 * on the fixed canvas above, and that is what the server stores: small, exact, drawable at any size,
 * and checkable without decoding a file. No timing or pressure is collected, because neither proves
 * who held the mouse and both are personal data nobody here has a use for.
 *
 * **`touch-action: none` is what makes it usable on a phone.** Without it the first stroke scrolls
 * the page instead of drawing.
 *
 * The pad is white with dark ink in both themes, because it stands for the paper the signature would
 * otherwise have gone on — and because a signature drawn in white ink on a dark pad would be
 * invisible the moment anyone renders it on a document.
 */
export function SignaturePad({
    label,
    clearLabel,
    placeholder,
    disabled = false,
    onChange
}: {
    label: string;
    clearLabel: string;
    placeholder: string;
    disabled?: boolean;
    /** Called when a stroke ends or the pad is cleared: the strokes, or null when there is not enough ink. */
    onChange: (strokes: number[][][] | null) => void;
}) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const strokesRef = useRef<number[][][]>([]);
    const drawingRef = useRef(false);
    const [isEmpty, setIsEmpty] = useState(true);

    const redraw = useCallback(() => {
        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');

        if (canvas === null || context === null || context === undefined) {
            return;
        }

        context.clearRect(0, 0, canvas.width, canvas.height);
        drawStrokes(context, strokesRef.current, canvas.width / HANDWRITING_WIDTH);
    }, []);

    // The backing store follows the element's real size and the screen's pixel density, so the ink
    // is sharp on a phone and the strokes survive a rotation or a resized window.
    useEffect(() => {
        const canvas = canvasRef.current;

        if (canvas === null) {
            return;
        }

        const resize = () => {
            const ratio = window.devicePixelRatio || 1;
            const rect = canvas.getBoundingClientRect();

            canvas.width = Math.max(1, Math.round(rect.width * ratio));
            canvas.height = Math.max(1, Math.round(rect.height * ratio));
            redraw();
        };

        resize();

        const observer = new ResizeObserver(resize);
        observer.observe(canvas);

        return () => observer.disconnect();
    }, [redraw]);

    function toPoint(event: PointerEvent<HTMLCanvasElement> | globalThis.PointerEvent): number[] {
        const rect = canvasRef.current!.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * HANDWRITING_WIDTH;
        const y = ((event.clientY - rect.top) / rect.height) * HANDWRITING_HEIGHT;

        return [clamp(Math.round(x), HANDWRITING_WIDTH), clamp(Math.round(y), HANDWRITING_HEIGHT)];
    }

    function pointCount(): number {
        return strokesRef.current.reduce((total, stroke) => total + stroke.length, 0);
    }

    function start(event: PointerEvent<HTMLCanvasElement>) {
        if (disabled || pointCount() >= MAX_POINTS) {
            return;
        }

        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);

        drawingRef.current = true;
        strokesRef.current.push([toPoint(event)]);
        setIsEmpty(false);
        redraw();
    }

    function move(event: PointerEvent<HTMLCanvasElement>) {
        if (!drawingRef.current) {
            return;
        }

        const stroke = strokesRef.current[strokesRef.current.length - 1];

        // A fast stroke delivers several positions per frame; taking them all keeps curves round
        // instead of turning them into polygons.
        const events = event.nativeEvent.getCoalescedEvents?.() ?? [event.nativeEvent];

        for (const coalesced of events) {
            if (pointCount() >= MAX_POINTS) {
                break;
            }

            const point = toPoint(coalesced);
            const last = stroke[stroke.length - 1];

            if (Math.hypot(point[0] - last[0], point[1] - last[1]) >= MIN_STEP) {
                stroke.push(point);
            }
        }

        redraw();
    }

    function end() {
        if (!drawingRef.current) {
            return;
        }

        drawingRef.current = false;
        onChange(hasEnoughInk(strokesRef.current) ? strokesRef.current.map((stroke) => [...stroke]) : null);
    }

    function clear() {
        strokesRef.current = [];
        setIsEmpty(true);
        redraw();
        onChange(null);
    }

    return (
        <div>
            <div className="relative aspect-[5/2] w-full overflow-hidden rounded-xl border-2 border-dashed border-slate-300 bg-white dark:border-slate-600">
                {/* The line a signature sits on, as on paper. Decoration only. */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-[8%] bottom-[22%] border-b border-slate-300"
                />

                {isEmpty && (
                    <p
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-sm text-slate-400"
                    >
                        {placeholder}
                    </p>
                )}

                <canvas
                    ref={canvasRef}
                    role="img"
                    aria-label={label}
                    onPointerDown={start}
                    onPointerMove={move}
                    onPointerUp={end}
                    onPointerCancel={end}
                    className={`absolute inset-0 size-full touch-none ${disabled ? 'cursor-not-allowed' : 'cursor-crosshair'}`}
                />
            </div>

            <div className="mt-2 flex justify-end">
                <button
                    type="button"
                    onClick={clear}
                    disabled={disabled || isEmpty}
                    className="text-sm font-semibold text-brand-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-brand-300"
                >
                    {clearLabel}
                </button>
            </div>
        </div>
    );
}

function clamp(value: number, max: number): number {
    return Math.min(max, Math.max(0, value));
}

/** The client's copy of the server's rule, so the button stays off until the server would say yes. */
function hasEnoughInk(strokes: number[][][]): boolean {
    const points = strokes.flat();

    if (points.length === 0) {
        return false;
    }

    const xs = points.map((point) => point[0]);
    const ys = points.map((point) => point[1]);

    return Math.max(...xs) - Math.min(...xs) >= MIN_EXTENT || Math.max(...ys) - Math.min(...ys) >= MIN_EXTENT;
}

/**
 * Each stroke as a curve through the midpoints of its segments, which is what makes a mouse-drawn
 * signature look drawn rather than plotted. A single point is a dot.
 */
function drawStrokes(context: CanvasRenderingContext2D, strokes: number[][][], scale: number) {
    context.strokeStyle = '#0f172a';
    context.fillStyle = '#0f172a';
    context.lineWidth = INK_WIDTH * scale;
    context.lineCap = 'round';
    context.lineJoin = 'round';

    for (const stroke of strokes) {
        const [first] = stroke;

        if (stroke.length === 1) {
            context.beginPath();
            context.arc(first[0] * scale, first[1] * scale, (INK_WIDTH * scale) / 2, 0, Math.PI * 2);
            context.fill();
            continue;
        }

        context.beginPath();
        context.moveTo(first[0] * scale, first[1] * scale);

        for (let index = 1; index < stroke.length - 1; index++) {
            const [x, y] = stroke[index];
            const [nextX, nextY] = stroke[index + 1];

            context.quadraticCurveTo(x * scale, y * scale, ((x + nextX) / 2) * scale, ((y + nextY) / 2) * scale);
        }

        const [lastX, lastY] = stroke[stroke.length - 1];
        context.lineTo(lastX * scale, lastY * scale);
        context.stroke();
    }
}
